import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, ADMIN_EMAIL } from '../../auth/AuthContext';
import { CAPABILITIES, ROLE_DISPLAY, roleDefaultCapabilities } from '../../auth/capabilities';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';
import { supabase } from '../../lib/supabase';
import db from '../../lib/database';
import { IconPlus, IconTrash, IconMail, IconX, IconEdit, IconCheck, IconRefresh, IconChevronRight } from '../../components/icons';
import { Button, IconButton, RowSkeleton } from '../../components/ui';

// Avatar background color per role — keeps the visual hierarchy readable
// at a glance (owner > super_admin > admin > office).
const ROLE_AVATAR_BG = {
    owner:       'bg-amber-500',
    super_admin: 'bg-red-700',
    admin:       'bg-red-500',
    office:      'bg-blue-600',
};

// Role pill style — used both inline in the user row and on the disabled
// Owner pill for the primary owner. Same palette as the avatar but lighter.
const ROLE_PILL_STYLES = {
    owner:       'bg-amber-100 text-amber-800 border-amber-300',
    super_admin: 'bg-red-100 text-red-800 border-red-300',
    admin:       'bg-rose-100 text-rose-800 border-rose-300',
    office:      'bg-blue-100 text-blue-800 border-blue-300',
};

// Visible roles in the dropdown — `hidden` roles (currently just owner)
// are filtered out. Owner is never user-selectable; only set via DB.
const SELECTABLE_ROLES = ROLE_DISPLAY.filter(r => !r.hidden);

// Group capabilities by their `group` field for the override section. Order
// preserved from capabilities.js so it reads the same everywhere.
function groupCapabilities() {
    const groups = [];
    const seen = new Map();
    for (const cap of CAPABILITIES) {
        if (!seen.has(cap.group)) {
            const g = { name: cap.group, capabilities: [] };
            seen.set(cap.group, g);
            groups.push(g);
        }
        seen.get(cap.group).capabilities.push(cap);
    }
    return groups;
}
const CAPABILITY_GROUPS = groupCapabilities();

// ---- RoleDropdown ---------------------------------------------------------
// Native <select> aligns the selected option with the trigger position, so
// when the last option is selected the menu opens upward and obscures the
// trigger. This custom dropdown is portal-rendered, always opens downward
// (or upward only if there's no room below), and keeps the trigger visible.
function RoleDropdown({ value, options, onChange, disabled, pillClasses }) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (!triggerRef.current?.contains(e.target)) setOpen(false);
        };
        const escHandler = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', handler);
        document.addEventListener('keydown', escHandler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('keydown', escHandler);
        };
    }, [open]);

    const handleToggle = () => {
        if (disabled) return;
        if (open) { setOpen(false); return; }
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Always anchor below trigger. If the menu would overflow viewport,
        // fall back to anchoring above.
        const menuHeight = options.length * 36 + 8;
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow >= menuHeight
            ? rect.bottom + 4
            : Math.max(8, rect.top - menuHeight - 4);
        setMenuPos({ top, left: rect.left, width: Math.max(rect.width, 140) });
        setOpen(true);
    };

    const currentLabel = options.find(o => o.key === value)?.label || value;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${pillClasses} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:brightness-95'}`}
            >
                <span>{currentLabel}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>
            {open && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                        minWidth: menuPos.width,
                        zIndex: 100,
                    }}
                    className="bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden py-1"
                >
                    {options.map(opt => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => { onChange(opt.key); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center gap-2 hover:bg-slate-100 transition-colors ${value === opt.key ? 'bg-slate-50' : ''}`}
                        >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ROLE_AVATAR_BG[opt.key] || 'bg-slate-300'}`}></span>
                            <span className="flex-1">{opt.label}</span>
                            {value === opt.key && <IconCheck size={12} className="text-emerald-600 flex-shrink-0"/>}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
}

// ---- PermCheckbox ---------------------------------------------------------
// Native <input type="checkbox"> renders inconsistently under our Tailwind CDN
// setup — sometimes the check indicator is invisible entirely. We've hit this
// before on the walkthrough portal's stairs/elevator toggles. This button-
// styled toggle gives us a definite, explicit visual state.
function PermCheckbox({ checked, locked, onChange, label, sublabel, badge }) {
    const interactive = !locked;
    return (
        <button
            type="button"
            onClick={() => interactive && onChange(!checked)}
            disabled={!interactive}
            className={`w-full text-left flex items-start gap-2.5 p-2 rounded-md text-xs transition-colors ${
                interactive ? 'cursor-pointer hover:bg-white' : 'cursor-default'
            } ${checked && !locked ? 'bg-emerald-50/60' : ''}`}
            title={sublabel}
        >
            {/* The visual checkbox — explicit box + checkmark icon so it
                renders identically regardless of native form styling. */}
            <span
                className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    checked
                        ? (locked ? 'bg-slate-400 border-slate-400' : 'bg-emerald-600 border-emerald-600')
                        : 'bg-white border-slate-300'
                }`}
            >
                {checked && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className={`font-semibold leading-tight block ${locked ? 'text-slate-500' : 'text-slate-800'}`}>
                    {label}
                    {badge && <span className={`ml-1.5 text-[9px] font-bold uppercase tracking-wider ${badge.color}`}>{badge.text}</span>}
                </span>
                {sublabel && <span className="text-[11px] text-slate-500 leading-snug mt-0.5 block">{sublabel}</span>}
            </span>
        </button>
    );
}

function UserRolesManager() {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const { user, can } = useAuth();
    const { show: showToast } = useToast();
    const [profiles, setProfiles] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [expandedProfileId, setExpandedProfileId] = useState(null);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviteRole, setInviteRole] = useState('office');
    const [sending, setSending] = useState(false);
    const [setupLinkInfo, setSetupLinkInfo] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const [editingNameId, setEditingNameId] = useState(null);
    const [editingNameValue, setEditingNameValue] = useState('');
    const [regeneratingId, setRegeneratingId] = useState(null);
    const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

    const loadData = async () => {
        setLoading(true);
        try {
            const [profileData, inviteData] = await Promise.all([
                db.getUserProfilesAdmin().catch(err => {
                    console.warn('Failed to load user profiles:', err.message);
                    return [];
                }),
                db.getInvites().catch(err => {
                    console.warn('Failed to load invites:', err.message);
                    return [];
                }),
            ]);
            setProfiles(profileData || []);
            setInvites(inviteData || []);
        } catch (e) {
            showToast('Error loading users: ' + e.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // ---- Role change handler ---------------------------------------------
    // Blocks demoting the owner (only changeable via DB) and blocks promoting
    // anyone to owner from the UI (also only via DB — owner is the "break
    // glass" role).
    const handleRoleChange = async (profile, newRole) => {
        if (profile.role === 'owner') {
            showToast('Owner role can only be changed by a database admin.', 'error');
            return;
        }
        if (newRole === 'owner') {
            showToast('Owner role can only be granted by a database admin.', 'error');
            return;
        }
        if (profile.email?.toLowerCase() === ADMIN_EMAIL && newRole !== 'owner') {
            showToast('Cannot change the primary owner role.', 'error');
            return;
        }
        setUpdating(profile.id);
        try {
            const updated = await db.updateUserRole(profile.id, newRole);
            setProfiles(prev => prev.map(p => p.id === profile.id ? { ...p, ...updated } : p));
            const roleLabel = ROLE_DISPLAY.find(r => r.key === newRole)?.label || newRole;
            showToast(`${profile.display_name || profile.email} is now ${roleLabel}`);
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setUpdating(null);
        }
    };

    // ---- Permission override toggle --------------------------------------
    // Adds or removes a capability from the user's permission_overrides
    // array. We treat the role's default capabilities as locked (not
    // toggleable — they always show as granted because the role grants them).
    const handleToggleOverride = async (profile, capability, granted) => {
        const current = Array.isArray(profile.permission_overrides) ? profile.permission_overrides : [];
        const next = granted
            ? [...new Set([...current, capability])]
            : current.filter(c => c !== capability);
        setUpdating(profile.id);
        try {
            const updated = await db.updateUserProfile(profile.id, { permission_overrides: next });
            setProfiles(prev => prev.map(p => p.id === profile.id ? { ...p, ...updated } : p));
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setUpdating(null);
        }
    };

    const startRename = (profile) => {
        setEditingNameId(profile.id);
        setEditingNameValue(profile.display_name || '');
    };

    const cancelRename = () => {
        setEditingNameId(null);
        setEditingNameValue('');
    };

    const saveRename = async (profile) => {
        const newName = editingNameValue.trim();
        if (!newName) {
            showToast('Name cannot be empty', 'error');
            return;
        }
        if (newName === (profile.display_name || '')) {
            cancelRename();
            return;
        }
        setUpdating(profile.id);
        try {
            const updated = await db.updateUserProfile(profile.id, { display_name: newName });
            setProfiles(prev => prev.map(p => p.id === profile.id ? { ...p, ...updated } : p));
            showToast(`Renamed to ${newName}`);
            cancelRename();
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setUpdating(null);
        }
    };

    const handleRegenerateLink = async (invite) => {
        setRegeneratingId(invite.id);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const accessToken = session?.access_token;
            if (!accessToken) throw new Error('Not signed in — please refresh and try again');

            const resp = await fetch('/api/invite-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    email: invite.email,
                    display_name: invite.display_name || invite.email.split('@')[0],
                    role: invite.role || 'office',
                }),
            });
            const result = await resp.json();
            if (!resp.ok) throw new Error(result?.error || `Regenerate failed (${resp.status})`);

            await db.updateInviteToken(invite.id, {
                setup_token: result.setup_token,
                setup_token_expires_at: result.setup_token_expires_at,
                user_id: result.user_id || invite.user_id,
                status: 'pending',
            });

            const setupUrl = `${window.location.origin}/?setup=${result.setup_token}`;
            setSetupLinkInfo({
                email: invite.email,
                url: setupUrl,
                expiresAt: result.setup_token_expires_at ? new Date(result.setup_token_expires_at) : null,
            });
            setLinkCopied(false);
            showToast('New setup link generated');
            loadData();
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setRegeneratingId(null);
        }
    };

    const handleCancelInvite = async (invite) => {
        if (!(await confirmDialog({ title: `Cancel the invite for ${invite.email}?`, body: `The pending setup link sent to ${invite.email} stops working and the invite is removed. You can send a new invite later.`, confirmLabel: 'Cancel invite', cancelLabel: 'Keep it', danger: true }))) return;
        try {
            await db.deleteInvite(invite.id);
            setInvites(prev => prev.filter(i => i.id !== invite.id));
            showToast('Invite cancelled');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        }
    };

    const handleDelete = async (profile) => {
        if (profile.user_id === user.id) {
            showToast('You cannot delete your own account', 'error');
            return;
        }
        if (profile.role === 'owner') {
            showToast('Cannot remove an owner-role user.', 'error');
            return;
        }
        if (!(await confirmDialog({ title: `Remove ${profile.display_name || profile.email}?`, body: `${profile.display_name || profile.email} loses access to the app and comes off the user list. Records they created keep their name.`, confirmLabel: 'Remove user', danger: true }))) return;
        try {
            await db.deleteUserProfile(profile.id);
            setProfiles(prev => prev.filter(p => p.id !== profile.id));
            showToast('User removed');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        }
    };

    const handleInvite = async () => {
        const email = inviteEmail.trim().toLowerCase();
        if (!email || !email.includes('@')) { showToast('Enter a valid email address', 'error'); return; }
        if (profiles.find(p => (p.email || '').toLowerCase() === email)) { showToast('This person already has an account', 'error'); return; }

        setSending(true);
        try {
            const displayName = inviteName.trim() || email.split('@')[0];

            const { data: { session } } = await supabase.auth.getSession();
            const accessToken = session?.access_token;
            if (!accessToken) throw new Error('Not signed in — please refresh and try again');

            const resp = await fetch('/api/invite-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ email, display_name: displayName, role: inviteRole }),
            });
            const result = await resp.json();
            if (!resp.ok) {
                throw new Error(result?.error || `Invite failed (${resp.status})`);
            }

            await db.createInvite({
                email: result.email || email,
                display_name: result.display_name || displayName,
                role: result.role || inviteRole,
                status: 'pending',
                user_id: result.user_id,
                setup_token: result.setup_token,
                setup_token_expires_at: result.setup_token_expires_at,
            });

            const setupUrl = `${window.location.origin}/?setup=${result.setup_token}`;
            setSetupLinkInfo({
                email: result.email || email,
                url: setupUrl,
                expiresAt: result.setup_token_expires_at ? new Date(result.setup_token_expires_at) : null,
            });
            setLinkCopied(false);
            setInviteEmail('');
            setInviteName('');
            setInviteRole('office');
            setShowInviteForm(false);

            loadData();
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setSending(false);
        }
    };

    const copySetupLink = async () => {
        if (!setupLinkInfo) return;
        try {
            await navigator.clipboard.writeText(setupLinkInfo.url);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (e) {
            showToast('Copy failed', 'error');
        }
    };

    if (!can('manage_users')) return <div className="p-6 text-center text-slate-400">You don't have permission to manage users and roles.</div>;

    const pendingInvites = invites.filter(i => i.status === 'pending');

    // ---- Permissions panel renderer --------------------------------------
    // Rendered below an expanded user row. Shows the capabilities granted
    // by the role (locked) and any additional permissions that can be
    // toggled on. Owner skips this entirely (they have everything).
    const renderPermissionsPanel = (profile) => {
        const role = profile.role || 'office';
        if (role === 'owner') {
            return (
                <div className="text-xs text-amber-700 italic px-4 py-3 bg-amber-50 border-t border-amber-100">
                    Owner — has access to everything, present and future. No overrides needed.
                </div>
            );
        }
        const grantedByRole = new Set(roleDefaultCapabilities(role));
        const overrides = new Set(Array.isArray(profile.permission_overrides) ? profile.permission_overrides : []);
        return (
            <div className="px-4 py-4 bg-slate-50/70 border-t border-slate-200 space-y-4">
                <div className="text-xs text-slate-500">
                    <span className="font-semibold">Granted by role</span> are locked — change the role to remove. <span className="font-semibold">Additional</span> permissions are extras you can grant on top.
                </div>
                {CAPABILITY_GROUPS.map(group => (
                    <div key={group.name}>
                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">{group.name}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                            {group.capabilities.map(cap => {
                                const fromRole = grantedByRole.has(cap.name);
                                const fromOverride = overrides.has(cap.name);
                                const checked = fromRole || fromOverride;
                                const locked = fromRole;
                                let badge = null;
                                if (locked) badge = { text: 'via role', color: 'text-slate-400' };
                                else if (fromOverride) badge = { text: 'override', color: 'text-emerald-600' };
                                return (
                                    <PermCheckbox
                                        key={cap.name}
                                        checked={checked}
                                        locked={locked || updating === profile.id}
                                        onChange={(next) => handleToggleOverride(profile, cap.name, next)}
                                        label={cap.label}
                                        sublabel={cap.description}
                                        badge={badge}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-1">
                        <h2 className="text-lg font-bold text-slate-900">Users & Roles</h2>
                        <Button variant="primary" onClick={() => setShowInviteForm(true)} leftIcon={<IconPlus size={15}/>}>Invite User</Button>
                    </div>
                    <p className="text-sm text-slate-500">Invite-only access. Roles set the floor — use the permissions panel on each row to grant extras.</p>
                </div>

                <div className="px-6 pb-6">
                    {loading ? (
                        <RowSkeleton rows={5} height="h-14" />
                    ) : profiles.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">No active users yet. Invite someone to get started.</p>
                    ) : (
                        <div className="space-y-2">
                            {profiles.map(profile => {
                                const email = profile.email || '';
                                const isCurrentUser = profile.user_id === user?.id;
                                const isEditing = editingNameId === profile.id;
                                const isExpanded = expandedProfileId === profile.id;
                                const role = profile.role || 'office';
                                const isOwnerRole = role === 'owner';
                                const overrideCount = Array.isArray(profile.permission_overrides) ? profile.permission_overrides.length : 0;
                                return (
                                    <div key={profile.id} className={`rounded-lg border ${isCurrentUser ? 'border-red-200 bg-red-50/30' : 'border-slate-200 bg-white'} overflow-hidden`}>
                                        <div className="flex items-center justify-between p-4">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${ROLE_AVATAR_BG[role] || 'bg-slate-500'}`}>
                                                    {(profile.display_name || email || '??').slice(0, 2).toUpperCase()}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    {isEditing ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <input
                                                                autoFocus
                                                                className="text-sm font-semibold text-slate-800 px-2 py-1 rounded border border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500 min-w-0 flex-1"
                                                                value={editingNameValue}
                                                                onChange={e => setEditingNameValue(e.target.value)}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') saveRename(profile);
                                                                    if (e.key === 'Escape') cancelRename();
                                                                }}
                                                                placeholder="Display name"
                                                            />
                                                            <IconButton
                                                                size="sm"
                                                                label="Save name"
                                                                onClick={() => saveRename(profile)}
                                                                disabled={updating === profile.id}
                                                            >
                                                                <IconCheck size={15}/>
                                                            </IconButton>
                                                            <IconButton
                                                                size="sm"
                                                                label="Cancel"
                                                                onClick={cancelRename}
                                                            >
                                                                <IconX size={15}/>
                                                            </IconButton>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="text-sm font-semibold text-slate-800 truncate">{profile.display_name || email.split('@')[0]}</span>
                                                            <IconButton
                                                                size="sm"
                                                                label="Edit name"
                                                                onClick={() => startRename(profile)}
                                                            >
                                                                <IconEdit size={13}/>
                                                            </IconButton>
                                                            {isCurrentUser && <span className="text-xs text-slate-400 flex-shrink-0">(you)</span>}
                                                        </div>
                                                    )}
                                                    <p className="text-xs text-slate-400 truncate">{email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {/* Role control. Owner is non-editable — shown as a fixed pill.
                                                    All other roles drop down to Office / Admin / Super Admin. */}
                                                {isOwnerRole ? (
                                                    <span className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border ${ROLE_PILL_STYLES.owner}`} title="Owner role is database-only">
                                                        Owner
                                                    </span>
                                                ) : (
                                                    <RoleDropdown
                                                        value={role}
                                                        options={SELECTABLE_ROLES}
                                                        onChange={(next) => handleRoleChange(profile, next)}
                                                        disabled={isCurrentUser || updating === profile.id}
                                                        pillClasses={ROLE_PILL_STYLES[role] || ROLE_PILL_STYLES.office}
                                                    />
                                                )}
                                                {/* Expand/collapse permissions panel */}
                                                <Button
                                                    variant="tertiary"
                                                    size="sm"
                                                    onClick={() => setExpandedProfileId(isExpanded ? null : profile.id)}
                                                    title={isExpanded ? 'Hide permissions' : 'Manage permissions'}
                                                    rightIcon={<IconChevronRight size={13} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}/>}
                                                >
                                                    <span className="text-[10px] font-semibold uppercase tracking-wide">
                                                        {isOwnerRole ? 'All' : overrideCount > 0 ? `${overrideCount} extra` : 'Perms'}
                                                    </span>
                                                </Button>
                                                {!isOwnerRole && !isCurrentUser && (
                                                    <IconButton
                                                        size="sm"
                                                        tone="danger"
                                                        label="Remove user"
                                                        onClick={() => handleDelete(profile)}
                                                    >
                                                        <IconTrash size={15}/>
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>
                                        {isExpanded && renderPermissionsPanel(profile)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {pendingInvites.length > 0 && (
                    <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                        <h3 className="text-sm font-bold text-slate-700 mb-3 mt-4 flex items-center gap-2">
                            <IconMail size={14}/> Pending Invites <span className="text-xs text-slate-400 font-medium">({pendingInvites.length})</span>
                        </h3>
                        <div className="space-y-2">
                            {pendingInvites.map(inv => {
                                const displayLabel = inv.display_name || (inv.email || '').split('@')[0];
                                const isRegenerating = regeneratingId === inv.id;
                                const inviteRoleLabel = ROLE_DISPLAY.find(r => r.key === inv.role)?.label || inv.role || 'office';
                                return (
                                    <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50/50 gap-3">
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-amber-400 text-white font-bold text-xs flex-shrink-0">
                                                {(displayLabel || '??').slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-slate-800 truncate">{displayLabel}</div>
                                                <p className="text-xs text-amber-700 truncate">{inv.email} &middot; Invited as {inviteRoleLabel} &middot; awaiting setup</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleRegenerateLink(inv)}
                                                loading={isRegenerating}
                                                title="Generate a new setup link"
                                                leftIcon={<IconRefresh size={13}/>}
                                            >
                                                New Link
                                            </Button>
                                            <IconButton
                                                size="sm"
                                                tone="danger"
                                                label="Cancel invite"
                                                onClick={() => handleCancelInvite(inv)}
                                            >
                                                <IconTrash size={15}/>
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {showInviteForm && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Invite New User</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-bold text-slate-600">Email Address</label>
                            <input className={inputCls} type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@example.com" autoFocus />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-600">Display Name</label>
                            <input className={inputCls} value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="First Last (optional)" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-600 mb-1.5 block">Role</label>
                            <div className="grid grid-cols-3 gap-2">
                                {SELECTABLE_ROLES.map(r => (
                                    <button key={r.key} type="button" onClick={() => setInviteRole(r.key)}
                                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                                            inviteRole === r.key
                                                ? `${ROLE_PILL_STYLES[r.key]?.replace('bg-', 'border-').replace(/text-\w+-\d+/, '')} bg-opacity-50 ${ROLE_PILL_STYLES[r.key]}`
                                                : 'border-slate-200 hover:border-slate-300'
                                        }`}>
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className={`w-2 h-2 rounded-full ${ROLE_AVATAR_BG[r.key] || 'bg-slate-400'}`}></span>
                                            <span className="text-sm font-semibold">{r.label}</span>
                                        </div>
                                        <span className="text-[11px] text-slate-500 block leading-tight">{r.description}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="secondary" onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteName(''); }}>Cancel</Button>
                            <Button variant="primary" onClick={handleInvite} loading={sending} disabled={!inviteEmail.trim()} leftIcon={<IconMail size={15}/>}>Send Invite</Button>
                        </div>
                    </div>
                </div>
            )}

            {setupLinkInfo && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Setup link ready</h3>
                    <div className="space-y-4">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                            Account created for <span className="font-semibold">{setupLinkInfo.email}</span>. Send them this link.
                            {setupLinkInfo.expiresAt && (
                                <div className="text-xs text-green-700 mt-1">Expires {setupLinkInfo.expiresAt.toLocaleDateString()} at {setupLinkInfo.expiresAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Setup link</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    readOnly
                                    value={setupLinkInfo.url}
                                    onFocus={e => e.target.select()}
                                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono bg-slate-50 text-slate-700"
                                />
                                <Button variant={linkCopied ? 'success' : 'primary'} onClick={copySetupLink}>
                                    {linkCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="secondary" onClick={() => setSetupLinkInfo(null)}>Done</Button>
                        </div>
                    </div>
                </div>
            )}
            {confirmElement}
        </div>
    );
}

export default UserRolesManager;
